/**
 * Appearance is three attributes on <html>, kept in this browser:
 *   data-theme    dark | light | system     → colour tokens (scalty-theme.css)
 *   data-density  compact | comfortable             → spacing and control heights
 *   data-scale    small | default | large | largest → root font-size and rem geometry
 */
import { ref, watch } from 'vue'

export type Theme = 'dark' | 'light' | 'system'
export type Density = 'compact' | 'comfortable'
export type Scale = 'small' | 'default' | 'large' | 'largest'

const KEY = 'scalty.appearance'
const state = { theme: ref<Theme>('dark'), density: ref<Density>('comfortable'), scale: ref<Scale>('default') }
let started = false

function apply() {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  el.dataset.theme = state.theme.value
  el.dataset.system = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  el.dataset.density = state.density.value
  el.dataset.scale = state.scale.value
  try { localStorage.setItem(KEY, JSON.stringify({ theme: state.theme.value, density: state.density.value, scale: state.scale.value })) } catch { /* This browser may refuse persistence; the selected appearance still applies. */ }
}

export function useAppearance() {
  if (!started && typeof window !== 'undefined') {
    started = true
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(KEY) ?? '{}')
      if (saved && typeof saved === 'object') {
        if ('theme' in saved && ['dark','light','system'].includes(String(saved.theme))) state.theme.value = saved.theme as Theme
        if ('density' in saved && ['compact','comfortable'].includes(String(saved.density))) state.density.value = saved.density as Density
        if ('scale' in saved && ['small','default','large','largest'].includes(String(saved.scale))) state.scale.value = saved.scale as Scale
      }
    } catch { /* Invalid or unavailable saved preferences keep the theme defaults. */ }
    apply()
    watch([state.theme, state.density, state.scale], apply)
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', apply)
  }
  return state
}
